// Heatmap zoom — continuous px-per-bin scale, level-of-detail columns and the zoom bar's
// view window. Pure, no Angular.
//
// Zoom = `step`, px per 15s bin (bar + 1px gap). Bar k's left edge sits at k*step inside
// the content div, time t at t/15*step. The scroller pads the content by `pad` on both
// sides; the «view» is the time window of the inner area between the paddings — at
// scrollLeft 0 / max it lands exactly on 0 / the end of the bars.

export const BIN_SEC = 15;
// Deep zoom for editing clip edges on the heatmap (it replaced the clip editor's own
// track, which showed ~±30s around the clip): 256 px per bin ≈ 0.06 s/px.
export const STEP_MAX = 256;
export const STEP_LOD = 3;      // bar 2px — below this bins are drawn in groups
export const STEP_DEFAULT = 5;  // bar 4px
const STEP_FLOOR = 1 / 64;
const STEP_FIT_MAX = 64;        // a few-minute stream doesn't get 400px bars

/** Steps are multiples of 1/64 px: Blink lays out in 1/64 px units, so a quantized bar
 *  width is exact and n bars end at exactly n*step. Unquantized, a 2.37px bar is laid out
 *  as 2.359 and 1000 bars drift ~11px off the ruler / lane / region positions. */
export function quantizeStep(step: number): number {
  return Math.max(STEP_FLOOR, Math.round(step * 64) / 64);
}

/** Step at which the whole stream fills the inner width exactly (rounded down to 1/64). */
export function fitStep(binCount: number, innerWidth: number): number {
  if (binCount <= 0 || innerWidth <= 0) return STEP_DEFAULT;
  // n bars + (n-1) gaps = n*step - 1 px must fit innerWidth.
  return Math.min(STEP_FIT_MAX, Math.max(STEP_FLOOR, Math.floor((innerWidth + 1) / binCount * 64) / 64));
}

export interface ZoomLimits {
  min: number;
  max: number;
}

/** Zoomed all the way out = the whole stream fills the width — never narrower, which left
 *  empty space right of the bars (a 1.5h stream zoomed out to 2px bars filled 60% of it).
 *  The fit is capped at STEP_FIT_MAX < STEP_MAX, so every stream can still zoom in. Unknown
 *  width (not measured yet) → no lower limit. */
export function zoomLimits(binCount: number, innerWidth: number): ZoomLimits {
  if (binCount <= 0 || innerWidth <= 0) return { min: STEP_FLOOR, max: STEP_MAX };
  const fit = fitStep(binCount, innerWidth);
  return { min: fit, max: Math.max(STEP_MAX, fit) };
}

export function clampStep(step: number, limits: ZoomLimits): number {
  return quantizeStep(Math.min(limits.max, Math.max(limits.min, step)));
}

// Zoom bar double-click «whole stream ⇄ detail»: detail = the first of these clearly
// closer than the fit — not STEP_MAX, which would jump a short stream to 256px bars.
export const DETAIL_LADDER = [STEP_DEFAULT, 13, 40, 128];

export function detailStep(fit: number, limits: ZoomLimits): number {
  return DETAIL_LADDER.find(s => s > fit * 1.5 && s <= limits.max) ?? limits.max;
}

/** Bins per drawn column: 1 down to STEP_LOD, then groups so a column stays ≥ STEP_LOD px —
 *  a 0.7px bar gets snapped away by the browser and takes its peak with it. */
export function binsPerColumn(step: number): number {
  return step >= STEP_LOD ? 1 : Math.ceil(STEP_LOD / step);
}

export interface HeatmapBinLike {
  startTime: number;
  messageCount: number;
}

export interface HeatmapColumn<B extends HeatmapBinLike> {
  bin: B;            // the busiest bin of the group — its height, click target, readout
  startTime: number; // first bin
  endTime: number;   // START of the last bin (same convention as peak endTime)
  n: number;         // bins in the column (the last one may be short)
  count: number;     // max messageCount in the group
}

/** Bins are dense from 0 (bin i starts at i*15), so column c covers bins [c*k, (c+1)*k). */
export function buildColumns<B extends HeatmapBinLike>(bins: B[], k: number): HeatmapColumn<B>[] {
  const out: HeatmapColumn<B>[] = [];
  const size = Math.max(1, Math.floor(k));
  for (let i = 0; i < bins.length; i += size) {
    const last = Math.min(i + size, bins.length) - 1;
    let peak = bins[i];
    for (let j = i + 1; j <= last; j++) if (bins[j].messageCount > peak.messageCount) peak = bins[j];
    out.push({ bin: peak, startTime: bins[i].startTime, endTime: bins[last].startTime, n: last - i + 1, count: peak.messageCount });
  }
  return out;
}

export interface ViewWindow {
  start: number;
  end: number;
}

/** Visible time window of the scroller, seconds clamped to [0, endSec]. */
export function viewWindow(scrollLeft: number, clientWidth: number, step: number, endSec: number, pad: number): ViewWindow {
  if (!(endSec > 0)) return { start: 0, end: 0 };
  if (!(clientWidth > 0) || !(step > 0)) return { start: 0, end: endSec };
  const toSec = (px: number) => px / step * BIN_SEC;
  const start = Math.min(endSec, Math.max(0, toSec(scrollLeft)));
  // +1: the last bar has no trailing gap — at max scroll the view ends exactly at endSec.
  const end = Math.min(endSec, Math.max(start, toSec(scrollLeft + clientWidth - 2 * pad + 1)));
  return { start, end };
}

/** scrollLeft that puts `startSec` at the inner area's left edge. */
export function scrollLeftForStart(startSec: number, step: number): number {
  return startSec / BIN_SEC * step;
}

/** Zoom-bar edge drag: show [start, end] — `edge` is the one under the pointer, the other
 *  stays put. The step clamp (zoom limits) moves the dragged edge back, never the fixed one.
 *  Quantized DOWN so the requested window always fits — rounded up, «0 … end» overflowed
 *  by a few px and the whole stream wasn't quite visible. */
export function zoomToWindow(
  win: ViewWindow, edge: 'start' | 'end', innerWidth: number, limits: ZoomLimits,
): { step: number; scrollLeft: number } {
  const span = Math.max(1, win.end - win.start);
  const step = clampStep(Math.floor((innerWidth + 1) * BIN_SEC / span * 64) / 64, limits);
  const scrollLeft = edge === 'end'
    ? scrollLeftForStart(win.start, step)
    : scrollLeftForStart(win.end, step) - (innerWidth + 1);
  return { step, scrollLeft: Math.max(0, scrollLeft) };
}
